import React from 'react';

import { decode } from 'html-entities';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { Navbar, Nav, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { business } from 'assets';
import { ShieldIcon, QuestionIcon, ExitIcon } from 'assets/icons';
import { formatDropdownOptions } from 'utils/Object';
import { Dropdown } from 'components/Forms';
import { Settings, MessagingPanel } from 'pages/Dashboard/Common';
import { getPath } from 'routes';
import { useRole } from 'hooks';
import { AccessRoles } from 'constants/enums';
import { Avatar } from 'components/UI';
import { useLogout } from 'hooks';
import { fetchQuestionnaires } from 'store/groups/service';
import AvatarNameColor from 'components/UI/AvatarNameColor';
import {
  setSelectedGid,
  getAuthorizedOrgs,
  getAuthorizedSubgroups,
  getGroupDisclaimer,
  getOrgStatus,
  getAvatar,
  getCurrentGroup,
  selectGroups,
  selectCurrentGid
} from 'store/groups/reducer';
import { resetCurrentPatientsView, resetDefaultPatientsViewFilters } from 'store/settings/reducer';
import {
  getOrgStatusDisclaimerColor,
  getOrgStatusDisclaimerMsg,
  QuestionnaireScope
} from 'constants/constants';
import instance from 'configs/requests';
import { useAppSelector } from 'hooks';

const TopNav = () => {
  const intl = useIntl();
  const hideNavigation = useAppSelector((state) => state.settings.hideNavigation);
  const dispatch = useDispatch();
  const { logout } = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const displayName = useAppSelector((state) => state.oauth.data?.displayName);
  const groups = useAppSelector(selectGroups);
  const aid = useAppSelector((state) => state.oauth.data?.aid);
  const user = useAppSelector((state) => state.oauth.data);
  const userGroups = useAppSelector(selectGroups)
    .filter((group) => group.gid !== 'all')
    .map((group) => {
      return { gid: group.gid, name: group.name, parentGid: group.name };
    });
  const selectedGroup = useAppSelector(getCurrentGroup);
  const selectedGid = useAppSelector(selectCurrentGid);
  const groupAvatar = useAppSelector(getAvatar);
  const messagingDisabled: boolean = selectedGroup?.patientEngageChannelEnabled === 0;
  const currentPatientGid = useAppSelector((state) => state.patient.data?.gid);
  const isAdmin = useRole([AccessRoles.ADMIN]);
  const isSysop = useRole([AccessRoles.SYSOP]);

  const organizations = useAppSelector(getAuthorizedOrgs);
  const subgroups = useAppSelector(getAuthorizedSubgroups);
  const allGroups = groups.filter((g) => g.gid === 'all');
  const organizationsDropdownOptions = organizations
    ? formatDropdownOptions(organizations, 'gid', 'name')
    : [];
  const subgroupsDropdownOptions = subgroups ? formatDropdownOptions(subgroups, 'gid', 'name') : [];
  const allGroupsDropdownOptions = allGroups ? formatDropdownOptions(allGroups, 'gid', 'name') : [];
  if (allGroupsDropdownOptions.length) {
    allGroupsDropdownOptions[0].label = intl.formatMessage({
      id: 'allGroupsKey'
    });
  }
  const disclaimer = useAppSelector(getGroupDisclaimer);
  const orgStatus = useAppSelector(getOrgStatus);
  const statusDisclaimer = getOrgStatusDisclaimerMsg(orgStatus || '');
  const statusDisclaimerColor = getOrgStatusDisclaimerColor(orgStatus || '');
  const navigationHidden = useAppSelector((state) => state.settings.hideNavigation);

  // Group by organization/subgroups and add the 'All Groups' option at the top
  // if the user has access to more than one organization or no org and multiple subgroups
  const dropdownOptions: ISelectGroupedOptions[] =
    organizations.length > 1 || (organizations.length === 0 && subgroups.length > 1)
      ? [
          {
            options: allGroupsDropdownOptions
          }
        ]
      : [];
  if (organizationsDropdownOptions.length) {
    dropdownOptions.push({
      label: intl.formatMessage({ id: 'organizationKey' }),
      options: organizationsDropdownOptions
    });
  }
  if (subgroupsDropdownOptions.length) {
    dropdownOptions.push({
      label: intl.formatMessage({ id: 'groupsKey' }),
      options: subgroupsDropdownOptions
    });
  }
  const dropdownOptionsFlat = allGroupsDropdownOptions
    .concat(organizationsDropdownOptions)
    .concat(subgroupsDropdownOptions);

  const handleChange = (option: ISelectOption) => {
    dispatch(setSelectedGid(option.value));
    dispatch(resetDefaultPatientsViewFilters());
    dispatch(resetCurrentPatientsView());

    if (option.value !== 'all') {
      dispatch(fetchQuestionnaires({ gid: option.value, scope: QuestionnaireScope.ALL }));
    }

    if (location.pathname !== getPath().dashboard.onboard) {
      const pathname = currentPatientGid
        ? getPath().dashboard.patients
        : location.pathname.replace(selectedGid, option.value);
      navigate(pathname, { replace: true });
    }
  };

  const handleZendesk = async () => {
    try {
      // Get JWT for clinician
      const payload = {
        userId: user?.aid,
        email: user?.emailAddress,
        name: user?.displayName,
        groups: userGroups
      };
      const { data } = await instance.post('/clinician-gateway/support/jwt', payload);
      window.open(
        `https://caresimplehelp.zendesk.com/access/jwt?jwt=${data}&return_to=https://caresimplehelp.zendesk.com/hc`,
        '_blank'
      );
    } catch (e) {
      window.open('https://caresimplehelp.zendesk.com/hc', '_blank');
    }
  };

  return (
    <div id="topNav" className={navigationHidden ? 'no-nav' : ''}>
      <Alert
        variant="blue-light"
        className="hipaa d-flex justify-content-center align-items-center"
      >
        <ShieldIcon className="icon me-2" style={{ fontSize: '14px' }} />
        <p className="m-0 p-0">
          {intl.formatMessage({
            id: 'HIPAAdisclaimer'
          })}
        </p>
      </Alert>
      <Navbar
        id="topNavigationBar"
        className="justify-content-between shadow-sm p-0 pt-2 pb-2 bg-white"
      >
        <div className="d-flex ms-4 select-group align-items-center">
          {!hideNavigation && (
            <>
              <Avatar
                size={30}
                className="flex-shrink-1 me-3 img-responsive"
                dataTestId="imgGroupAvatar"
                imgUrl={groupAvatar || business}
                rounded={groupAvatar ? false : true}
                isDefault={!groupAvatar}
              />
              {groups.length > 0 && (
                <Dropdown
                  dataTestId="groupMainDropDown"
                  options={dropdownOptions}
                  value={dropdownOptionsFlat.find((item) => item.value === selectedGid)}
                  onChange={handleChange}
                  className="flex-grow-1"
                  variant="transparent"
                />
              )}
            </>
          )}
        </div>
        <Nav className="d-inline-flex align-items-center">
          <Nav.Item className="me-5 d-flex align-items-center">
            <AvatarNameColor name={displayName || ''} size={30} dataTestId="imgClinicianAvatar" />
            <span data-test="clinicianName" className="ms-2 me-1">
              {displayName}
            </span>
          </Nav.Item>
          {isAdmin && !messagingDisabled && (
            <Nav.Item data-test="messages-panel-btn" className="me-4">
              <MessagingPanel />
            </Nav.Item>
          )}
          {/* PHX-709 - ensure aid is defined as a flag for login completed. TODO: rework login flow from EHR launch */}
          {aid && !isSysop && (
            <Nav.Item className="me-4">
              <Settings />
            </Nav.Item>
          )}
          {!isSysop && (
            <Nav.Item className="me-4" onClick={handleZendesk}>
              <QuestionIcon className="icon iconTopNav" />
            </Nav.Item>
          )}
          <Nav.Item onClick={logout} className="me-4">
            <ExitIcon className="icon iconTopNav" data-test="exitBtn" />
          </Nav.Item>
        </Nav>
      </Navbar>
      {disclaimer && (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip
              id="group-disclaimer"
              data-test="group-disclaimer-tooltip"
              className="tooltip-min-w-300"
            >
              {decode(disclaimer)}
            </Tooltip>
          }
        >
          <Alert
            data-test="group-disclaimer"
            variant="danger"
            className="mb-0 white-space-nowrap text-overflow-ellipsis overflow-hidden"
          >
            {decode(disclaimer)}
          </Alert>
        </OverlayTrigger>
      )}
      {statusDisclaimer && (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip
              id="org-status-disclaimer"
              data-test="org-status-tooltip"
              className="tooltip-min-w-300"
            >
              {intl.formatMessage({
                id: statusDisclaimer
              })}
            </Tooltip>
          }
        >
          <Alert
            data-test-value={orgStatus}
            data-test="org-status-disclaimer"
            variant={statusDisclaimerColor}
            className="mb-0 white-space-nowrap text-overflow-ellipsis overflow-hidden"
          >
            {intl.formatMessage({
              id: statusDisclaimer
            })}
          </Alert>
        </OverlayTrigger>
      )}
    </div>
  );
};

export default TopNav;
